import { useEffect, useCallback, useRef } from 'react';
import useSocketEventManager from '../../hooks/useSocketEventManager';

/**
 * Socket event manager specifically for ConversationView
 * Handles all socket events and provides callbacks to parent component
 * Extracted from ConversationView.js to reduce component complexity
 */
const ConversationSocketManager = ({
  socket,
  selectedConversation,
  onAiSuggestion,
  onNewMessage,
  onModeChange,
  onModeChangeSuggestion,
  onAiGoalProposal,
  onAiAssessmentProposal,
  onClientGoalActionUpdate,
  onCareNoteAlert,
  onSocketConnected
}) => {
  const { 
    addEventListener, 
    removeEventListener, 
    emitEvent,
    isConnected 
  } = useSocketEventManager(socket);

  // Use refs to store stable references to handler functions
  const handlersRef = useRef({
    onAiSuggestion,
    onNewMessage,
    onModeChange,
    onModeChangeSuggestion,
    onAiGoalProposal,
    onAiAssessmentProposal,
    onClientGoalActionUpdate,
    onCareNoteAlert,
    onSocketConnected
  });

  // Update handlers ref when props change
  useEffect(() => {
    handlersRef.current = {
      onAiSuggestion,
      onNewMessage,
      onModeChange,
      onModeChangeSuggestion,
      onAiGoalProposal,
      onAiAssessmentProposal,
      onClientGoalActionUpdate,
      onCareNoteAlert,
      onSocketConnected
    };
  });

  // Socket connection status tracking
  useEffect(() => {
    if (onSocketConnected) {
      onSocketConnected(isConnected);
    }
  }, [isConnected, onSocketConnected]);

  // Handle AI suggestion events
  const handleAiSuggestion = useCallback((data) => {
    if (data.conversationId === selectedConversation?._id) {
      if (handlersRef.current.onAiSuggestion) {
        handlersRef.current.onAiSuggestion(data);
      }
    }
  }, [selectedConversation?._id]);

  // Handle new message events
  const handleNewMessage = useCallback((data) => {
    if (data.conversationId === selectedConversation?._id) {
      if (handlersRef.current.onNewMessage) {
        handlersRef.current.onNewMessage(data);
      }
    }
  }, [selectedConversation?._id]);

  // Handle mode change events
  const handleModeChange = useCallback((data) => {
    if (data.conversationId === selectedConversation?._id) {
      if (handlersRef.current.onModeChange) {
        handlersRef.current.onModeChange(data);
      }
    }
  }, [selectedConversation?._id]);

  // Handle mode change suggestion events
  const handleModeChangeSuggestion = useCallback((data) => {
    if (data.conversationId === selectedConversation?._id) {
      console.log("Received ai_mode_change_suggestion:", data);
      if (handlersRef.current.onModeChangeSuggestion) {
        handlersRef.current.onModeChangeSuggestion(data);
      }
    }
  }, [selectedConversation?._id]);

  // Handle AI goal proposal events
  const handleAiGoalProposal = useCallback((data) => {
    if (data.conversationId === selectedConversation?._id) {
      console.log("Received AI goal proposal for therapist:", data);
      if (handlersRef.current.onAiGoalProposal) {
        handlersRef.current.onAiGoalProposal(data);
      }
    }
  }, [selectedConversation?._id]);

  // Handle AI assessment proposal events
  const handleAiAssessmentProposal = useCallback((data) => {
    if (data.conversationId === selectedConversation?._id) {
      console.log("Received AI assessment proposal for therapist:", data);
      if (handlersRef.current.onAiAssessmentProposal) {
        handlersRef.current.onAiAssessmentProposal(data);
      }
    }
  }, [selectedConversation?._id]);

  // Handle client goal action update events
  const handleClientGoalActionUpdate = useCallback((data) => {
    if (data.conversationId === selectedConversation?._id) {
      if (handlersRef.current.onClientGoalActionUpdate) {
        handlersRef.current.onClientGoalActionUpdate(data);
      }
    }
  }, [selectedConversation?._id]);

  // Handle care note alert events
  const handleCareNoteAlert = useCallback((data) => {
    // Care note alerts are not conversation-specific, they're for the therapist
    if (handlersRef.current.onCareNoteAlert) {
      handlersRef.current.onCareNoteAlert(data);
    }
  }, []);

  // Set up event listeners when socket and conversation are available
  useEffect(() => {
    if (socket && selectedConversation) {
      // Add all event listeners
      addEventListener('ai_suggestion', handleAiSuggestion);
      addEventListener('new_message', handleNewMessage);
      addEventListener('control_mode_changed', handleModeChange);
      addEventListener('ai_mode_change_suggestion', handleModeChangeSuggestion);
      addEventListener('ai_goal_proposal_for_therapist', handleAiGoalProposal);
      addEventListener('ai_assessment_proposal_for_therapist', handleAiAssessmentProposal);
      addEventListener('client_goal_action_update', handleClientGoalActionUpdate);
      addEventListener('care_note_alert', handleCareNoteAlert);

      // Join conversation room
      emitEvent('therapist:join_conversation_room', selectedConversation._id);

      return () => {
        // Leave conversation room on cleanup
        emitEvent('therapist:leave_conversation_room', selectedConversation._id);
        
        // Remove event listeners
        removeEventListener('ai_suggestion');
        removeEventListener('new_message');
        removeEventListener('control_mode_changed');
        removeEventListener('ai_mode_change_suggestion');
        removeEventListener('ai_goal_proposal_for_therapist');
        removeEventListener('ai_assessment_proposal_for_therapist');
        removeEventListener('client_goal_action_update');
        removeEventListener('care_note_alert');
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    socket,
    selectedConversation?._id
  ]);

  // This component doesn't render anything - it's just for socket management
  return null;
};

export default ConversationSocketManager;